import Phaser from 'phaser';
import type { WireDef } from '../sim/types';
import { COLORS, TIMING, wireStyle } from './palette';

export class WireView {
  readonly def: WireDef;
  private scene: Phaser.Scene;
  private glow: Phaser.GameObjects.Graphics;
  private graphic: Phaser.GameObjects.Graphics;
  private shimmer: Phaser.GameObjects.Graphics;
  private energize: Phaser.GameObjects.Graphics;
  private energizeBoost = 0;
  private from: { x: number; y: number };
  private to: { x: number; y: number };
  private open: boolean;
  private style = wireStyle('normal');
  private shimmerT = 0;

  constructor(scene: Phaser.Scene, def: WireDef, from: { x: number; y: number }, to: { x: number; y: number }) {
    this.scene = scene;
    this.def = def;
    this.from = from;
    this.to = to;
    this.style = wireStyle(def.type ?? 'normal');
    this.open = def.type !== 'locked';
    this.glow = scene.add.graphics();
    this.glow.setBlendMode(Phaser.BlendModes.ADD);
    this.graphic = scene.add.graphics();
    this.shimmer = scene.add.graphics();
    this.shimmer.setBlendMode(Phaser.BlendModes.ADD);
    this.energize = scene.add.graphics();
    this.energize.setBlendMode(Phaser.BlendModes.ADD);
    this.draw();

    // Gentle dataflow shimmer along non-locked wires. Throttled.
    if (def.type !== 'forbidden') {
      let lastShim = -1;
      scene.tweens.addCounter({
        from: 0,
        to: 1,
        duration: 2400,
        repeat: -1,
        onUpdate: (tw) => {
          const v = tw.getValue() ?? 0;
          if (Math.abs(v - lastShim) < 0.025) return;
          lastShim = v;
          this.shimmerT = v;
          if (this.open) this.drawShimmer();
        },
      });
    }

    // Forbidden wires throb + march.
    if (def.type === 'forbidden') {
      scene.tweens.add({
        targets: this.glow,
        alpha: { from: 1, to: 0.45 },
        yoyo: true,
        repeat: -1,
        duration: 700,
        ease: 'Sine.easeInOut',
      });
      this.startForbiddenCrawl(scene);
    }
  }

  private startForbiddenCrawl(scene: Phaser.Scene) {
    let lastCrawl = -1;
    scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: 1400,
      repeat: -1,
      onUpdate: (tw) => {
        if (!this.open) return;
        const v = tw.getValue() ?? 0;
        if (Math.abs(v - lastCrawl) < 0.025) return;
        lastCrawl = v;
        this.drawForbiddenCrawl(v);
      },
    });
  }

  private drawForbiddenCrawl(t: number) {
    // Uses the shimmer layer for a marching-dash warning on forbidden wires.
    const s = this.shimmer;
    s.clear();
    if (this.def.type !== 'forbidden') return;
    const { x0, y0, x1, y1, len, ux, uy } = this.endpointsTrimmed();
    if (len < 30) return;
    const segCount = Math.max(3, Math.floor(len / 18));
    s.fillStyle(0xff5577, 0.85);
    for (let i = 0; i < segCount; i++) {
      const localT = (i / segCount + t) % 1;
      const p = localT * len;
      const px = x0 + ux * p;
      const py = y0 + uy * p;
      const size = 2 + Math.sin(localT * Math.PI) * 2.5;
      s.fillCircle(px, py, size);
    }
    // suppress unused-var warnings for the trimmed endpoints
    void x1; void y1;
  }

  private endpointsTrimmed() {
    const dx = this.to.x - this.from.x;
    const dy = this.to.y - this.from.y;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    // sit just inside the node rim (radius 51, so trim 54 gives ~3px gap)
    const trim = 54;
    return {
      x0: this.from.x + ux * trim,
      y0: this.from.y + uy * trim,
      x1: this.to.x - ux * trim,
      y1: this.to.y - uy * trim,
      ux, uy, len,
    };
  }

  private draw() {
    const g = this.graphic;
    const halo = this.glow;
    g.clear();
    halo.clear();

    const { x0, y0, x1, y1 } = this.endpointsTrimmed();

    const col = !this.open ? COLORS.wireLocked : this.style.color;
    const alpha = !this.open ? 0.55 : 1;

    // wide soft glow underlay
    if (this.open) {
      halo.lineStyle(10, this.style.color, 0.14);
      halo.lineBetween(x0, y0, x1, y1);
      halo.lineStyle(5, this.style.color, 0.22);
      halo.lineBetween(x0, y0, x1, y1);
    }

    g.lineStyle(this.style.width, col, alpha);

    if (this.style.dashed || !this.open) {
      this.drawDashed(g, x0, y0, x1, y1);
    } else {
      g.lineBetween(x0, y0, x1, y1);
    }

    // endpoint plugs — small bright disc + outer ring so the wire reads as
    // "socketed" into the node rim rather than floating next to it
    if (this.open) {
      g.fillStyle(this.style.color, 1);
      g.fillCircle(x0, y0, 3);
      g.fillCircle(x1, y1, 3);
      g.lineStyle(1, 0xffffff, 0.35);
      g.strokeCircle(x0, y0, 4);
      g.strokeCircle(x1, y1, 4);
    }

    if (this.style.arrow) {
      this.drawArrowHead(g, col, alpha);
    }

    if (!this.open) {
      this.drawLockPlate(g, (x0 + x1) / 2, (y0 + y1) / 2);
    }

    if (this.def.type === 'forbidden') {
      this.drawForbiddenMark(g, (x0 + x1) / 2, (y0 + y1) / 2);
    }
  }

  private drawShimmer() {
    if (this.def.type === 'forbidden') return; // forbidden owns the shimmer layer
    const s = this.shimmer;
    s.clear();
    if (!this.open) return;
    const { x0, y0, x1, y1, len } = this.endpointsTrimmed();
    if (len < 30) return;
    const ux = (x1 - x0) / len;
    const uy = (y1 - y0) / len;

    // a single soft highlight band slides along the wire
    const t = this.shimmerT;
    const headT = t;
    const bandLen = Math.min(60, len * 0.35);
    const headPos = -bandLen + (len + bandLen) * headT;
    const segments = 8;
    for (let i = 0; i < segments; i++) {
      const p = headPos + (i / segments) * bandLen;
      if (p < 0 || p > len) continue;
      const alpha = (i / segments) * 0.55;
      s.fillStyle(0xffffff, alpha);
      s.fillCircle(x0 + ux * p, y0 + uy * p, 1.8);
    }
  }

  private drawDashed(g: Phaser.GameObjects.Graphics, x0: number, y0: number, x1: number, y1: number) {
    const dx = x1 - x0;
    const dy = y1 - y0;
    const len = Math.hypot(dx, dy);
    const ux = dx / len;
    const uy = dy / len;
    const dash = 10;
    const gap = 8;
    let traveled = 0;
    while (traveled < len) {
      const a = Math.min(dash, len - traveled);
      const sx = x0 + ux * traveled;
      const sy = y0 + uy * traveled;
      const ex = x0 + ux * (traveled + a);
      const ey = y0 + uy * (traveled + a);
      g.lineBetween(sx, sy, ex, ey);
      traveled += dash + gap;
    }
  }

  private drawArrowHead(g: Phaser.GameObjects.Graphics, col: number, alpha: number) {
    const { x1, y1, ux, uy } = this.endpointsTrimmed();
    // double-chevron arrowhead for direction clarity, with a glow halo behind
    const tip = { x: x1, y: y1 };

    // outer halo
    this.glow.fillStyle(col, 0.25);
    this.glow.fillCircle(tip.x, tip.y, 12);

    // primary head
    const back = { x: x1 - ux * 16, y: y1 - uy * 16 };
    const left = { x: back.x - uy * 9, y: back.y + ux * 9 };
    const right = { x: back.x + uy * 9, y: back.y - ux * 9 };
    g.fillStyle(col, alpha);
    g.fillTriangle(tip.x, tip.y, left.x, left.y, right.x, right.y);

    // inner chevron echo for sci-fi feel
    const back2 = { x: x1 - ux * 8, y: y1 - uy * 8 };
    const tip2  = { x: x1 - ux * 2, y: y1 - uy * 2 };
    const lin = { x: back2.x - uy * 4, y: back2.y + ux * 4 };
    const rin = { x: back2.x + uy * 4, y: back2.y - ux * 4 };
    g.fillStyle(0xffffff, 0.45);
    g.fillTriangle(tip2.x, tip2.y, lin.x, lin.y, rin.x, rin.y);
  }

  private drawLockPlate(g: Phaser.GameObjects.Graphics, cx: number, cy: number) {
    // small padlock-ish badge in the middle of a locked wire
    g.fillStyle(0x0e1226, 1);
    g.fillRect(cx - 9, cy - 7, 18, 14);
    g.lineStyle(1.5, 0x6a728f, 1);
    g.strokeRect(cx - 9, cy - 7, 18, 14);
    g.lineStyle(1.5, 0x6a728f, 1);
    g.beginPath();
    g.arc(cx, cy - 7, 5, Math.PI, 0, false);
    g.strokePath();
  }

  private drawForbiddenMark(g: Phaser.GameObjects.Graphics, cx: number, cy: number) {
    g.fillStyle(0x140208, 1);
    g.fillCircle(cx, cy, 8);
    g.lineStyle(2, 0xff5577, 1);
    g.strokeCircle(cx, cy, 8);
    g.lineBetween(cx - 4, cy - 4, cx + 4, cy + 4);
    g.lineBetween(cx - 4, cy + 4, cx + 4, cy - 4);
  }

  open_(scene: Phaser.Scene) {
    if (this.open) return;
    this.open = true;
    this.draw();
    // bright sweep along the wire to indicate unlock
    this.playPulse({ fromId: this.def.a, toId: this.def.b }, TIMING.tickMs * 0.8, 0xffd966);
  }

  burn(scene: Phaser.Scene) {
    scene.tweens.add({
      targets: [this.graphic, this.glow, this.shimmer],
      alpha: 0.35,
      duration: 320,
    });
  }

  triggerForbidden(scene: Phaser.Scene) {
    scene.cameras.main.shake(280, 0.018);
    scene.tweens.add({
      targets: [this.graphic, this.glow],
      alpha: { from: 1, to: 0.3, yoyo: true },
      duration: 90,
      repeat: 4,
    });
  }

  /**
   * Animate a pulse traveling from `fromId` to `toId`.
   * Layers: wide halo + bright core dot + drifting sparks behind for a
   * proper "data packet" feel.
   */
  playPulse(
    direction: { fromId: string; toId: string },
    travelMs: number,
    color: number = COLORS.pulse,
  ): Phaser.GameObjects.Arc {
    const start = direction.fromId === this.def.a ? this.from : this.to;
    const end = direction.fromId === this.def.a ? this.to : this.from;

    this.playEnergize(travelMs);

    const halo = this.scene.add
      .circle(start.x, start.y, 22, color, 0.35)
      .setBlendMode(Phaser.BlendModes.ADD);

    const dot = this.scene.add
      .circle(start.x, start.y, 8, color, 1)
      .setBlendMode(Phaser.BlendModes.ADD);

    // bright white core dot for "molten" feel
    const core = this.scene.add
      .circle(start.x, start.y, 3.5, 0xffffff, 0.95)
      .setBlendMode(Phaser.BlendModes.ADD);

    // Comet tail: 3 lagged trail dots — visible without flooding the tween queue
    const tailCount = 3;
    const tailDots: Phaser.GameObjects.Arc[] = [];
    for (let i = 0; i < tailCount; i++) {
      const fade = 1 - i / tailCount;
      const r = 6 * fade + 1;
      const s = this.scene.add
        .circle(start.x, start.y, r, COLORS.pulseTrail, 0.55 * fade)
        .setBlendMode(Phaser.BlendModes.ADD);
      tailDots.push(s);
    }

    // sparks dripping off the leading edge
    const sparkCount = 2;
    const sparks: Phaser.GameObjects.Arc[] = [];
    for (let i = 0; i < sparkCount; i++) {
      const s = this.scene.add
        .circle(start.x, start.y, 1.6, color, 0.85)
        .setBlendMode(Phaser.BlendModes.ADD);
      sparks.push(s);
    }

    this.scene.tweens.add({
      targets: halo,
      x: end.x,
      y: end.y,
      alpha: 0,
      radius: 32,
      duration: travelMs,
      ease: 'Sine.easeIn',
      onComplete: () => halo.destroy(),
    });

    this.scene.tweens.add({
      targets: dot,
      x: end.x,
      y: end.y,
      duration: travelMs,
      ease: 'Sine.easeIn',
      onComplete: () => dot.destroy(),
    });

    this.scene.tweens.add({
      targets: core,
      x: end.x,
      y: end.y,
      duration: travelMs,
      ease: 'Sine.easeIn',
      onComplete: () => core.destroy(),
    });

    // Trail dots: each starts a little later so they string behind the head
    for (let i = 0; i < tailCount; i++) {
      const td = tailDots[i];
      const lag = (i + 1) / (tailCount + 4);
      this.scene.tweens.add({
        targets: td,
        x: end.x,
        y: end.y,
        alpha: 0,
        delay: lag * travelMs * 0.5,
        duration: travelMs * (1 - lag * 0.3),
        ease: 'Sine.easeIn',
        onComplete: () => td.destroy(),
      });
    }

    // sparks lag and scatter sideways
    for (let i = 0; i < sparkCount; i++) {
      const s = sparks[i];
      const delay = (i + 1) * (travelMs * 0.07);
      const drift = (Math.random() - 0.5) * 20;
      this.scene.tweens.add({
        targets: s,
        x: end.x + drift,
        y: end.y + drift,
        alpha: 0,
        duration: travelMs - delay,
        delay,
        ease: 'Sine.easeIn',
        onComplete: () => s.destroy(),
      });
    }

    return dot;
  }

  /** Every GameObject this wire owns — used for camera ignore routing. */
  gameObjects(): Phaser.GameObjects.GameObject[] {
    return [this.glow, this.graphic, this.shimmer, this.energize];
  }

  /**
   * Permanent hover-highlight. Used by GraphView when the player hovers a
   * connected node — brightens the wire so the connection is obvious.
   */
  setHovered(on: boolean) {
    if (!this.open) {
      // closed (locked) wires don't get hover highlight
      this.energizeBoost = 0;
      this.drawEnergize();
      return;
    }
    this.scene.tweens.killTweensOf(this);
    this.scene.tweens.add({
      targets: this,
      energizeBoost: on ? 0.55 : 0,
      duration: 160,
      onUpdate: () => this.drawEnergize(),
    });
  }

  /**
   * Briefly energize the wire — wide additive halo flares up and decays.
   * Called by `playPulse` so every traveling pulse leaves a visible wake
   * along the conductor.
   */
  private playEnergize(travelMs: number) {
    if (!this.open) return;
    this.energizeBoost = 1;
    this.drawEnergize();
    this.scene.tweens.add({
      targets: this,
      energizeBoost: 0,
      duration: travelMs * 1.4,
      ease: 'Cubic.easeOut',
      onUpdate: () => this.drawEnergize(),
      onComplete: () => {
        this.energizeBoost = 0;
        this.drawEnergize();
      },
    });
  }

  private drawEnergize() {
    const g = this.energize;
    g.clear();
    if (!this.open || this.energizeBoost <= 0.01) return;
    const { x0, y0, x1, y1 } = this.endpointsTrimmed();
    const k = this.energizeBoost;
    g.lineStyle(16 * k, this.style.color, 0.18 * k);
    g.lineBetween(x0, y0, x1, y1);
    g.lineStyle(8 * k, 0xffffff, 0.20 * k);
    g.lineBetween(x0, y0, x1, y1);
  }

  destroy() {
    this.graphic.destroy();
    this.glow.destroy();
    this.shimmer.destroy();
    this.energize.destroy();
  }
}
